import { useEffect, useState } from 'react';

/**
 * True once the browser is idle (or the visitor has started interacting),
 * never before. Used to keep below-fold, non-critical widgets (chat, review
 * card, Perfect Storm popup) out of the initial hydration pass entirely —
 * this always starts `false` on both server and client, so it introduces no
 * SSR/hydration mismatch and never suspends the boot render (see
 * src/main.tsx's note on hydration not being allowed to suspend). The
 * lazy-loaded widget only mounts, and its chunk only downloads, after this
 * flips to `true`, which happens strictly after hydration commits.
 *
 * @param timeout ms to wait for requestIdleCallback before mounting anyway
 *   (its fallback setTimeout on browsers without requestIdleCallback).
 */
export function useDeferredMount(timeout = 2500): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;
    let settled = false;
    let idleHandle: number | undefined;
    let timeoutHandle: number | undefined;

    const mark = () => {
      if (settled) return;
      settled = true;
      setReady(true);
      cleanup();
    };

    const interactionEvents: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    interactionEvents.forEach((evt) => window.addEventListener(evt, mark, { once: true, passive: true }));

    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
      .requestIdleCallback;
    const cic = (window as Window & { cancelIdleCallback?: (handle: number) => void }).cancelIdleCallback;

    if (ric) {
      idleHandle = ric(mark, { timeout });
    } else {
      timeoutHandle = window.setTimeout(mark, timeout);
    }

    function cleanup() {
      interactionEvents.forEach((evt) => window.removeEventListener(evt, mark));
      if (idleHandle !== undefined && cic) cic(idleHandle);
      if (timeoutHandle !== undefined) window.clearTimeout(timeoutHandle);
    }

    return cleanup;
  }, [ready, timeout]);

  return ready;
}
